import { Theme, Typography, styled } from "@mui/material";
import React from "react";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    body3: React.CSSProperties;
    body4: React.CSSProperties;
    body5: React.CSSProperties;
    h5light: React.CSSProperties;
    h6light: React.CSSProperties;
    subtitle3: React.CSSProperties;
    block12: React.CSSProperties;
    subtitle1heavy: React.CSSProperties;
    xxs: React.CSSProperties;
    xxsSemiBold: React.CSSProperties;
    xxsBold: React.CSSProperties;
    xs: React.CSSProperties;
    xsSemiBold: React.CSSProperties;
    xsBold: React.CSSProperties;
    sm: React.CSSProperties;
    smSemiBold: React.CSSProperties;
    smBold: React.CSSProperties;
    base: React.CSSProperties;
    baseSemiBold: React.CSSProperties;
    baseBold: React.CSSProperties;
    lg: React.CSSProperties;
    lgSemiBold: React.CSSProperties;
    lgBold: React.CSSProperties;
    xl: React.CSSProperties;
    xlSemiBold: React.CSSProperties;
    xlBold: React.CSSProperties;
    xl2: React.CSSProperties;
    xl2SemiBold: React.CSSProperties;
    xl2Bold: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    body4?: React.CSSProperties;
    body5?: React.CSSProperties;
    h5light?: React.CSSProperties;
    h6light?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
    block12?: React.CSSProperties;
    subtitle1heavy?: React.CSSProperties;
    xxs?: React.CSSProperties;
    xxsSemiBold?: React.CSSProperties;
    xxsBold?: React.CSSProperties;
    xs?: React.CSSProperties;
    xsSemiBold?: React.CSSProperties;
    xsBold?: React.CSSProperties;
    sm?: React.CSSProperties;
    smSemiBold?: React.CSSProperties;
    smBold?: React.CSSProperties;
    base?: React.CSSProperties;
    baseSemiBold?: React.CSSProperties;
    baseBold?: React.CSSProperties;
    lg?: React.CSSProperties;
    lgSemiBold?: React.CSSProperties;
    lgBold?: React.CSSProperties;
    xl?: React.CSSProperties;
    xlSemiBold?: React.CSSProperties;
    xlBold?: React.CSSProperties;
    xl2?: React.CSSProperties;
    xl2SemiBold?: React.CSSProperties;
    xl2Bold?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    body5: true;
    subtitle3: true;
    h5light: true;
    h6light: true;
    block12: true;
    subtitle1heavy: true;
    xxs: true;
    xxsSemiBold: true;
    xxsBold: true;
    xs: true;
    xsSemiBold: true;
    xsBold: true;
    sm: true;
    smSemiBold: true;
    smBold: true;
    base: true;
    baseSemiBold: true;
    baseBold: true;
    lg: true;
    lgSemiBold: true;
    lgBold: true;
    xl: true;
    xlSemiBold: true;
    xlBold: true;
    xl2: true;
    xl2SemiBold: true;
    xl2Bold: true;
  }
}

const typography: Partial<Theme["typography"]> = {
  fontSize: 12,
  fontFamily: "Inter,arial,sans-serif",
  h1: {
    fontWeight: "bolder",
    fontSize: "96px",
    lineHeight: "126px",
  },
  h2: {
    fontWeight: "bolder",
    fontSize: "60px",
    lineHeight: "72px",
  },
  h3: {
    fontWeight: "bolder",
    fontSize: "48px",
    lineHeight: "56px",
  },
  h4: {
    fontWeight: "bolder",
    fontSize: "34px",
    lineHeight: "36px",
  },
  h5: {
    fontSize: "24px",
    lineHeight: "24px",
    fontWeight: "bold",
  },
  h5light: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "24px",
    fontWeight: 500,
    lineHeight: "24px",
  },
  h6: {
    fontSize: "20px",
    fontWeight: "bold",
    lineHeight: "24px",
  },
  h6light: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "20px",
    fontWeight: 500,
    lineHeight: "24px",
  },
  subtitle1: {
    fontSize: "16px",
    lineHeight: "24px",
  },
  subtitle1heavy: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
  },
  subtitle2: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "24px",
  },
  subtitle3: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "14px",
    fontWeight: "bold",
    lineHeight: "20px",
  },
  body1: {
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
  },
  body2: {
    fontSize: "14px",
    lineHeight: "24px",
  },
  body3: {
    fontFamily: "Inter,arial,sans-serif",
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "20px",
  },
  body4: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "12px",
    lineHeight: "16px",
  },
  body5: {
    fontFamily: "Inter,arial,sans-serif",
    fontSize: "10px",
    lineHeight: "20px",
  },
  block12: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "12px",
    lineHeight: "16px",
  },
  caption: {
    fontSize: "12px",
    lineHeight: "20px",
    fontWeight: "bold",
  },
  button: {
    fontWeight: "bolder",
    fontSize: "14px",
    lineHeight: "16px",
  },
  overline: {
    fontSize: "10px",
    lineHeight: "16px",
    textTransform: "uppercase",
  },
  xxs: {
    fontSize: "10px",
    lineHeight: "14px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xxsSemiBold: {
    fontSize: "10px",
    fontWeight: 600,
    lineHeight: "14px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xxsBold: {
    fontSize: "10px",
    lineHeight: "14px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  xs: {
    fontSize: "12px",
    lineHeight: "16px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xsSemiBold: {
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xsBold: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  sm: {
    fontSize: "14px",
    lineHeight: "20px",
    fontFamily: "Inter,arial,sans-serif",
  },
  smSemiBold: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    fontFamily: "Inter,arial,sans-serif",
  },
  smBold: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  base: {
    fontSize: "16px",
    lineHeight: "24px",
    fontFamily: "Inter,arial,sans-serif",
  },
  baseSemiBold: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 600,
    fontFamily: "Inter,arial,sans-serif",
  },
  baseBold: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  lg: {
    fontSize: "18px",
    lineHeight: "28px",
    fontFamily: "Inter,arial,sans-serif",
  },
  lgSemiBold: {
    fontSize: "18px",
    lineHeight: "28px",
    fontWeight: 600,
    fontFamily: "Inter,arial,sans-serif",
  },
  lgBold: {
    fontSize: "18px",
    lineHeight: "28px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  xl: {
    fontSize: "20px",
    lineHeight: "28px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xlSemiBold: {
    fontSize: "20px",
    lineHeight: "28px",
    fontWeight: 600,
    fontFamily: "Inter,arial,sans-serif",
  },
  xlBold: {
    fontSize: "20px",
    lineHeight: "28px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
  xl2: {
    fontSize: "24px",
    lineHeight: "32px",
    fontFamily: "Inter,arial,sans-serif",
  },
  xl2SemiBold: {
    fontSize: "24px",
    lineHeight: "32px",
    fontWeight: 600,
    fontFamily: "Inter,arial,sans-serif",
  },
  xl2Bold: {
    fontSize: "24px",
    lineHeight: "32px",
    fontWeight: 700,
    fontFamily: "Inter,arial,sans-serif",
  },
};

export default typography;

/** @deprecated use a standard p tag with tailwind classes */
export const P = styled(Typography, {
  shouldForwardProp: () => true,
  skipSx: false,
})(({ theme }) => ({
  color: theme.palette.grey[900],
}));
